
import React from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '@/components/utils/PageWrapper';
import AnimatedElement from '@/components/utils/AnimatedElement';
import { Cloud, ArrowLeft, ChevronRight } from 'lucide-react';
import AnimatedLink from '@/components/ui/animated-link';
import { motion } from 'framer-motion';

const CloudInfrastructurePage = () => {
  return (
    <PageWrapper>
      <main className="flex-grow pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="mb-6">
            <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to all services
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="md:col-span-2">
              <AnimatedElement animation="fadeIn">
                <div className="bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-400 p-3 rounded-xl inline-block mb-4">
                  <Cloud className="h-10 w-10" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                  Cloud <span className="text-primary">Infrastructure</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-6">
                  Scalable cloud solutions to store, process, and analyze massive data sets efficiently.
                </p>
              </AnimatedElement>

              <AnimatedElement animation="slideUp" delay={0.2}>
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p>
                    Cloud infrastructure provides the foundation for modern AI and data analytics applications. Our cloud solutions help businesses build scalable, secure, and cost-effective environments for storing, processing, and analyzing data.
                  </p>
                  
                  <h2>Our Approach</h2>
                  <p>
                    We design and implement cloud infrastructure solutions tailored to your specific needs. Whether you're migrating existing applications to the cloud or building new cloud-native solutions, we ensure your infrastructure is optimized for performance, security, and cost.
                  </p>
                  
                  <h2>Key Benefits</h2>
                  <ul>
                    <li>Scale resources up or down based on demand</li>
                    <li>Reduce capital expenditure on hardware and maintenance</li>
                    <li>Improve business agility and time-to-market</li>
                    <li>Enhance data security and compliance</li>
                    <li>Ensure high availability and disaster recovery</li>
                    <li>Optimize cloud costs through efficient resource utilization</li>
                  </ul>
                </div>
              </AnimatedElement>
            </div>

            <div className="md:col-span-1">
              <motion.div 
                className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <h3 className="text-xl font-semibold mb-4">Our Cloud Services</h3>
                <ul className="space-y-3">
                  {[
                    "Cloud Migration",
                    "Hybrid Cloud Solutions",
                    "Multi-cloud Strategy",
                    "Serverless Architecture",
                    "Container Orchestration",
                    "Cloud Security"
                  ].map((service, index) => (
                    <motion.li 
                      key={index}
                      className="flex items-start"
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>{service}</span>
                    </motion.li>
                  ))}
                </ul>

                <div className="mt-8">
                  <AnimatedLink to="/contact" fullWidth>
                    Contact Us
                  </AnimatedLink>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div 
            className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 p-8 rounded-2xl mb-16"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <h2 className="text-2xl font-semibold mb-4">Ready to optimize your cloud infrastructure?</h2>
            <p className="text-muted-foreground mb-6">
              Let's discuss how our cloud solutions can help you build a scalable, secure foundation for your applications.
            </p>
            <AnimatedLink to="/contact">
              Get Started
            </AnimatedLink>
          </motion.div>
        </div>
      </main>
    </PageWrapper>
  );
};

export default CloudInfrastructurePage;
